/**
 * Port of {@class FieldPaths} —
 * backend/src/main/java/com/irian/iam/ruleengine/api/FieldPaths.java
 *
 * Field-path string helpers shared by the parser, validator, and evaluator.
 */

/**
 * Walk `fieldPath` left-to-right tracking paren and bracket nesting depth,
 * invoking `onTopLevelDot(i)` at every `.` outside any `(...)` or `[...]`
 * group. Shared scanner for `splitFieldPath` / `lastTopLevelDot`.
 */
function scanTopLevelDots(fieldPath: string, onTopLevelDot: (i: number) => void): void {
    let parenDepth = 0
    let bracketDepth = 0
    for (let i = 0; i < fieldPath.length; i++) {
        const ch = fieldPath.charAt(i)
        if (ch === '(') parenDepth++
        else if (ch === ')') parenDepth--
        else if (ch === '[') bracketDepth++
        else if (ch === ']') bracketDepth--
        else if (ch === '.' && parenDepth === 0 && bracketDepth === 0) onTopLevelDot(i)
    }
}

/**
 * Split a field path on dots that are outside any (...) group. Used by both
 * RuleConditionParser (validator) and RuleConditionEvaluator (resolver).
 */
export function splitFieldPath(fieldPath: string): string[] {
    const parts: string[] = []
    let start = 0
    scanTopLevelDots(fieldPath, i => {
        parts.push(fieldPath.substring(start, i))
        start = i + 1
    })
    parts.push(fieldPath.substring(start))
    return parts
}

/**
 * Strip the (type='X') relationship-segment filter from a path segment.
 */
export function stripTypeFilter(segment: string): string {
    const paren = segment.indexOf('(')
    return paren < 0 ? segment : segment.substring(0, paren)
}

/**
 * Extract the type filter value from a segment of the form
 * "incomingRelationships(type='VALUE')". Returns null when no filter.
 */
export function extractTypeFilterValue(segment: string): string | null {
    const paren = segment.indexOf('(')
    if (paren < 0) return null
    const firstQuote = segment.indexOf("'", paren)
    const lastQuote = segment.lastIndexOf("'")
    if (firstQuote < 0 || lastQuote <= firstQuote) return null
    return segment.substring(firstQuote + 1, lastQuote)
}

/**
 * Strip an [id] row-reference suffix from a path segment.
 */
export function stripRowReference(segment: string): string {
    const bracket = segment.indexOf('[')
    return bracket < 0 ? segment : segment.substring(0, bracket)
}

/** Returns true when the segment encodes an entity[id] row reference. */
export function hasRowReference(segment: string): boolean {
    const open = segment.indexOf('[')
    return open >= 0 && segment.endsWith(']')
}

/**
 * Extract the raw id literal text from a segment of the form
 * "entity[idLiteral]".
 */
export function extractRowReferenceLiteral(segment: string): string | null {
    const open = segment.indexOf('[')
    if (open < 0 || !segment.endsWith(']')) return null
    return segment.substring(open + 1, segment.length - 1)
}

export const COUNT_SEGMENT = 'count'

/**
 * Returns true when fieldPath's last dot-segment is the reserved count
 * aggregator.
 */
export function endsWithCount(fieldPath: string): boolean {
    const parts = splitFieldPath(fieldPath)
    return parts.length >= 2 && parts[parts.length - 1] === COUNT_SEGMENT
}

/**
 * Drop a trailing .count segment from a field path.
 */
export function stripCountSuffix(fieldPath: string): string {
    const lastDot = lastTopLevelDot(fieldPath)
    return lastDot < 0 ? fieldPath : fieldPath.substring(0, lastDot)
}

/**
 * Locate the byte offset of path inside source expression, for use in error
 * positions. Returns 0 when not found.
 */
export function findPathPosition(expression: string, path: string): number {
    const idx = expression.indexOf(path)
    return idx >= 0 ? idx : 0
}

function lastTopLevelDot(fieldPath: string): number {
    let last = -1
    scanTopLevelDots(fieldPath, i => {last = i})
    return last
}
